import { useCallback, useEffect, useState } from 'react';
import { ProfileWithSubscription, Roles } from '@/types/auth/auth';

type ProfileFormData = {
  full_name: string;
  role: string;
  email: string;
  avatar: string | null | undefined;
};

type SubscriptionFormData = {
  status: string;
  quantity: number;
  extra_quantity: number;
  stripe_id: string | null;
  trial_ends_at: string | null;
  ends_at: string | null;
};

export const useProfileForm = (user: ProfileWithSubscription) => {
  const [formData, setFormData] = useState<ProfileFormData>({
    full_name: user.full_name || "",
    role: user.role || Roles.Consumer,
    email: user.email || "",
    avatar: user.avatar_url ?? null,
  });

  useEffect(() => {
    setFormData({
      full_name: user.full_name || "",
      role: user.role || Roles.Consumer,
      email: user.email || "",
      avatar: user.avatar_url ?? null,
    });
  }, [user.id, user.full_name, user.role, user.email, user.avatar_url]);

  const handleFieldChange = useCallback((field: keyof Omit<ProfileFormData, "avatar">, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleAvatarChange = useCallback((url: string | undefined) => {
    setFormData((prev) => ({ ...prev, avatar: url }));
  }, []);

  const isProfileChanged = useCallback(() => {
    return (
      formData.full_name !== user.full_name ||
      formData.role !== user.role ||
      formData.email !== user.email ||
      (formData.avatar ?? null) !== (user.avatar_url ?? null)
    );
  }, [formData, user]);

  return { formData, handleFieldChange, handleAvatarChange, isProfileChanged };
};

export const useSubscriptionForm = (user: ProfileWithSubscription) => {
  const defaultSubscription = user.subscription || user.subscriptions?.[0];
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<number | null>(defaultSubscription?.id ?? null);
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionFormData>({
    status: defaultSubscription?.status ?? "trialing",
    quantity: defaultSubscription?.quantity ?? 1,
    extra_quantity: defaultSubscription?.extra_quantity ?? 0,
    stripe_id: defaultSubscription?.stripe_id || null,
    trial_ends_at: defaultSubscription?.trial_ends_at || null,
    ends_at: defaultSubscription?.ends_at || null,
  });

  useEffect(() => {
    const subscription = user.subscription || user.subscriptions?.[0];
    setSubscriptionData({
      status: subscription?.status ?? "trialing",
      quantity: subscription?.quantity ?? 1,
      extra_quantity: subscription?.extra_quantity ?? 0,
      stripe_id: subscription?.stripe_id || null,
      trial_ends_at: subscription?.trial_ends_at || null,
      ends_at: subscription?.ends_at || null,
    });
    setSelectedSubscriptionId(subscription?.id ?? null);
  }, [user.subscription, user.subscriptions]);

  const handleSelectSubscription = useCallback(
    (subscriptionId: number) => {
      setSelectedSubscriptionId(subscriptionId);
      setIsCreatingNew(false);
      const selectedSub = user.subscriptions?.find((s) => s.id === subscriptionId);
      if (selectedSub) {
        setSubscriptionData({
          status: selectedSub.status ?? "trialing",
          quantity: selectedSub.quantity ?? 1,
          extra_quantity: selectedSub.extra_quantity ?? 0,
          stripe_id: selectedSub.stripe_id || null,
          trial_ends_at: selectedSub.trial_ends_at || null,
          ends_at: selectedSub.ends_at || null,
        });
      }
    },
    [user.subscriptions],
  );

  const handleSubscriptionFieldChange = useCallback((field: keyof SubscriptionFormData, value: string | number | null) => {
    setSubscriptionData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const startThirtyDayTrial = useCallback(() => {
    const trialEndDate = new Date();
    trialEndDate.setDate(trialEndDate.getDate() + 30);

    setIsCreatingNew(true);
    setSubscriptionData({
      status: "trialing",
      quantity: 5,
      extra_quantity: 0,
      stripe_id: null,
      trial_ends_at: trialEndDate.toISOString(),
      ends_at: null,
    });
  }, []);

  const giveFreeSubscription = useCallback(() => {
    setSelectedSubscriptionId(null);
    setIsCreatingNew(true);
    setSubscriptionData({
      status: "active",
      quantity: 0,
      extra_quantity: 10,
      stripe_id: null,
      trial_ends_at: null,
      ends_at: null,
    });
  }, []);

  const [isCreatingNew, setIsCreatingNew] = useState(false);

  const createNewSubscription = useCallback(() => {
    setSelectedSubscriptionId(null);
    setIsCreatingNew(true);
    setSubscriptionData({
      status: "active",
      quantity: 1,
      extra_quantity: 0,
      stripe_id: null,
      trial_ends_at: null,
      ends_at: null,
    });
  }, []);

  const isSubscriptionChanged = useCallback(() => {
    const targetSubscription = selectedSubscriptionId
      ? user.subscriptions?.find((s) => s.id === selectedSubscriptionId)
      : user.subscription || user.subscriptions?.[0];

    if (!targetSubscription) {
      return subscriptionData.status === "active" || subscriptionData.status === "trialing";
    }

    const normalizeEmpty = (val: string | null | undefined) => val || null;

    return (
      subscriptionData.status !== targetSubscription.status ||
      subscriptionData.quantity !== targetSubscription.quantity ||
      subscriptionData.extra_quantity !== (targetSubscription.extra_quantity ?? 0) ||
      subscriptionData.stripe_id !== (targetSubscription.stripe_id ?? "") ||
      normalizeEmpty(subscriptionData.trial_ends_at) !== (targetSubscription.trial_ends_at ?? null) ||
      normalizeEmpty(subscriptionData.ends_at) !== (targetSubscription.ends_at ?? null)
    );
  }, [subscriptionData, user.subscription, user.subscriptions, selectedSubscriptionId]);

  const resetSubscriptionData = useCallback(() => {
    const subscription = selectedSubscriptionId
      ? user.subscriptions?.find((s) => s.id === selectedSubscriptionId)
      : user.subscription || user.subscriptions?.[0];

    setIsCreatingNew(false);
    setSubscriptionData({
      status: subscription?.status ?? "trialing",
      quantity: subscription?.quantity ?? 1,
      extra_quantity: subscription?.extra_quantity ?? 0,
      stripe_id: subscription?.stripe_id ?? "",
      trial_ends_at: subscription?.trial_ends_at ?? "",
      ends_at: subscription?.ends_at ?? "",
    });
  }, [user.subscription, user.subscriptions, selectedSubscriptionId]);

  return {
    selectedSubscriptionId,
    subscriptionData,
    handleSelectSubscription,
    handleSubscriptionFieldChange,
    startThirtyDayTrial,
    giveFreeSubscription,
    createNewSubscription,
    isSubscriptionChanged,
    resetSubscriptionData,
    isCreatingNew,
  };
};
